// StatusBadge - analysis-status pill (StatusBadge) and report-verdict pill
// (VerdictBadge), each mapping a string to a colored, iconed label.

import { AlertTriangle, Ban, Check, Clock, HelpCircle, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/classnames';

export function StatusBadge({ status, stale }: { status?: string | null; stale?: boolean }) {
  // stale=true: gateway couldn't reach malbox, so the status is unknown.
  // Show a neutral "Refreshing" pill, not the terminal "unknown" verdict.
  if (stale || !status) {
    return (
      <span className={cn('pill', 'border-ink-700 text-ink-400 bg-ink-900/60')}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Refreshing
      </span>
    );
  }
  const map: Record<string, { label: string; cls: string; Icon: typeof Clock; spin?: boolean }> = {
    pending:             { label: 'Queued',       cls: 'border-ink-700 text-ink-300 bg-ink-900/60', Icon: Clock },
    initializing:        { label: 'Initializing', cls: 'border-accent-500/35 text-accent-200 bg-accent-500/[0.08]', Icon: Loader2, spin: true },
    running:             { label: 'Running',      cls: 'border-accent-500/35 text-accent-200 bg-accent-500/[0.08]', Icon: Loader2, spin: true },
    uploading:           { label: 'Uploading results', cls: 'border-accent-500/35 text-accent-200 bg-accent-500/[0.08]', Icon: Loader2, spin: true },
    stopping:            { label: 'Finalizing',   cls: 'border-accent-500/35 text-accent-200 bg-accent-500/[0.08]', Icon: Loader2, spin: true },
    completed:           { label: 'Completed',    cls: 'border-verdict-clean/40 text-verdict-clean bg-verdict-clean/[0.08]', Icon: Check },
    failed:              { label: 'Failed',       cls: 'border-verdict-malicious/40 text-verdict-malicious bg-verdict-malicious/[0.08]', Icon: X },
    canceled:            { label: 'Canceled',     cls: 'border-ink-700 text-ink-400 bg-ink-900/60', Icon: Ban },
    unknown:             { label: 'Unknown',      cls: 'border-ink-700 text-ink-400 bg-ink-900/60', Icon: AlertTriangle },
  };
  const cfg = map[status] ?? map.unknown;
  return (
    <span className={cn('pill', cfg.cls)}>
      <cfg.Icon className={cn('w-3.5 h-3.5', cfg.spin && 'animate-spin')} />
      {cfg.label}
    </span>
  );
}

export function VerdictBadge({ verdict }: { verdict?: string | null }) {
  const v = verdict ?? 'unknown';
  // 'unknown' gets a distinct treatment ("Inconclusive", dashed) - it's not a
  // verdict on the same axis as clean/suspicious/malicious.
  if (v === 'unknown') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full border border-dashed border-ink-600 text-ink-300 bg-ink-900/40 font-semibold whitespace-nowrap">
        <HelpCircle className="w-3.5 h-3.5" />
        Inconclusive
      </span>
    );
  }
  const map: Record<string, { label: string; cls: string }> = {
    clean:      { label: 'Clean',      cls: 'border-verdict-clean/50 text-verdict-clean bg-verdict-clean/10' },
    suspicious: { label: 'Suspicious', cls: 'border-verdict-suspicious/50 text-verdict-suspicious bg-verdict-suspicious/10' },
    malicious: {
      label: 'Malicious',
      cls: 'border-verdict-malicious/55 text-verdict-malicious bg-verdict-malicious/[0.12]',
    },
    bypassed: { label: 'Bypassed', cls: 'border-cyan-500/50 text-cyan-400 bg-cyan-500/10' },
  };
  const cfg = map[v] ?? map.clean;
  return (
    <span className={cn('pill text-sm px-3 py-1 font-semibold', cfg.cls)}>
      {cfg.label}
    </span>
  );
}
